import { CreditCard, Button, Icon, Toggle, Alert, LabelMenuOption } from '@chikara/ui';
import { Card as CardType } from '@/features/card/types/card.types';
import { CardDetailMetadata } from '@/features/card/components/CardDetailPage/CardDetailMetadata';
import styles from '../../pages/CardDetailPage/CardDetailPage.module.css';
import { t } from '@/i18n';

interface CardDetailActionsProps {
    card: CardType;
    accountData: any;
    state: {
        isTemporaryBlocked: boolean;
        isBlockedByAdmin: boolean;
        showNip: boolean;
        nipValue: string;
        isTagMenuOpen: boolean;
        aliasValue: string;

        tags: LabelMenuOption[];
        selectedTag?: string;
    };
    controls: {
        tagButtonRef: React.RefObject<HTMLDivElement | null>;
        setIsTagMenuOpen: (isOpen: boolean) => void;
        setIsCreateTagModalOpen: (isOpen: boolean) => void;
    };
    handlers: {
        handleToggleNipVisibility: () => void;
        getCardPinDetail: () => Promise<void>;
        handleToggleBlock: (checked: boolean) => void;
        handleChangeNip: () => void;
        handleEditAlias: () => void;
        handleTagChange: (value: string) => void;
        handleOpenManageTags: () => void;
        handleEditLabel: () => void;
    };
}

const maxTags = 10;

export const CardDetailActions = ({
    card,
    accountData,
    state,
    controls,
    handlers,
}: CardDetailActionsProps) => {
    const isActionsDisabled = card.status === 'disabled' || card.status === 'replaced' || !card.card_uuid;
    const showTemporaryBlockAlert = state.isTemporaryBlocked && !state.isBlockedByAdmin;
    const showBlockedByAdminAlert = state.isBlockedByAdmin;
    const showReplacedAlert = card.status === 'replaced';

    return (
        <div className={styles.cardSection}>
            <div className={styles.cardVisual}>
                <CreditCard
                    limit={card.creditLimit}
                    lastFourDigits={card.maskedPan.split(' ').pop() || ''}
                    isActive={card.status === 'enabled'}
                    isBlocked={state.isTemporaryBlocked}
                />
            </div>
            <div className={styles.cardControls}>
                <div className={styles.leftColumn}>
                    {(showTemporaryBlockAlert || showBlockedByAdminAlert || showReplacedAlert) && (
                        <div className={styles.alertsContainer}>
                            {showTemporaryBlockAlert && (
                                <Alert variant="warning" icon={<Icon name="warning" size="sm" />} className={styles.statusAlert}>
                                    {t('dashboard.card.detail.info.temporaryBlockAlert')}
                                </Alert>
                            )}
                            {showBlockedByAdminAlert && (
                                <Alert variant="warning" icon={<Icon name="warning" size="sm" />} className={styles.statusAlert}>
                                    {t('dashboard.card.detail.info.blockedByAdmin')}
                                </Alert>
                            )}
                            {showReplacedAlert && (
                                <Alert variant="info" icon={<Icon name="information" size="sm" />} className={styles.statusAlert}>
                                    {t('dashboard.card.detail.info.replacedAlert')}
                                </Alert>
                            )}
                        </div>
                    )}
                    <div className={styles.nipAndBlockContainer}>
                        <div className={styles.nipContainer}>
                            <label className={styles.fieldLabel}>{t('dashboard.card.detail.info.nip')}</label>
                            <div className={styles.nipContent}>
                                <span className={styles.nipValue}>
                                    {state.showNip && accountData.view.can_view_nip ? state.nipValue : '••••'}
                                </span>
                                <button
                                    type="button"
                                    onClick={handlers.handleToggleNipVisibility}
                                    className={styles.eyeButton}
                                    disabled={!accountData.view.can_view_nip}
                                    aria-label={state.showNip ? t('dashboard.card.detail.info.nipButton.hide') : t('dashboard.card.detail.info.nipButton.show')}
                                >
                                    <Icon
                                        name={state.showNip && accountData.view.can_view_nip ? 'eye-closed' : 'eye-on'}
                                        size="md"
                                        color={!accountData.view.can_view_nip ? 'warning' : 'disabled'}
                                        className={styles.eyeIcon}
                                    />
                                </button>
                            </div>
                        </div>
                        <div className={styles.separator} />
                        <div className={styles.blockToggle}>
                            <label className={styles.fieldLabel}>{t('dashboard.card.detail.info.temporaryBlock')}</label>
                            <Toggle
                                checked={state.isTemporaryBlocked}
                                onChange={handlers.handleToggleBlock}
                                size="md"
                                disabled={isActionsDisabled || (state.isTemporaryBlocked && !accountData.view.can_unblock)}
                            />
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="md"
                        onClick={handlers.handleChangeNip}
                        className={styles.changeNipButton}
                        disabled={isActionsDisabled || !accountData.view.can_update_nip}
                    >
                        {t('dashboard.card.detail.info.nipButton.change')}
                    </Button>
                </div>

                <CardDetailMetadata
                    card={card}
                    handleEditLabel={handlers.handleEditLabel}
                    onEditAlias={handlers.handleEditAlias}
                    onEditTags={handlers.handleOpenManageTags}
                    isTagMenuOpen={state.isTagMenuOpen}
                    setIsTagMenuOpen={controls.setIsTagMenuOpen}
                    tagButtonRef={controls.tagButtonRef}
                    tags={state.tags}
                    selectedTag={state.selectedTag}
                    onTagChange={handlers.handleTagChange}
                    onCreateTag={() => controls.setIsCreateTagModalOpen(true)}
                    createDisabled={state.tags.length >= maxTags}
                    createTooltip={state.tags.length >= maxTags ? t('dashboard.card.detail.tags.limitReached') || 'Límite de etiquetas alcanzado' : undefined}
                />
            </div>
        </div>
    );
};
